'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';

import { createSubscribeNewsletterSchema, SubscribeNewsletterValues } from '@/app/[locale]/schema';
import { subscribeNewsletter } from '@/app/[locale]/store/actions';

import { Button } from './ui/button';
import { Form, FormControl, FormField, FormItem, FormMessage } from './ui/form';
import { Input } from './ui/input';

export const SubscribeNewsletter = () => {
    const t = useTranslations();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const subscribeNewsletterSchema = createSubscribeNewsletterSchema(t);

    const form = useForm<SubscribeNewsletterValues>({
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: zodResolver(subscribeNewsletterSchema),
        defaultValues: {
            email: '',
        },
    });

    const onSubmit = async (data: SubscribeNewsletterValues) => {
        setIsSubmitting(true);

        try {
            await subscribeNewsletter(data.email);

            toast.success(t('subscribe-newsletter-success'));
        } catch {
            toast.error(t('subscribe-newsletter-error'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className='flex w-full flex-col gap-4 lg:mt-[-7.5px] lg:w-[291px] lg:gap-6'>
            <div className='flex flex-col gap-1 text-center lg:gap-2 lg:text-left'>
                <p className='text-red-coral text-[20px] font-[800] leading-[100%] tracking-[-2%] lg:text-left lg:text-4xl lg:font-bold lg:leading-[45px] lg:tracking-[-1.12px]'>
                    {t('subscribe-to')}
                </p>
                <p className='font-publico text-[20px] font-normal italic leading-[100%] tracking-[-2%] lg:text-4xl'>
                    {t('our-newsletter')}
                </p>
            </div>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <div className='flex w-full flex-col gap-4 lg:w-auto'>
                        <FormField
                            control={form.control}
                            name='email'
                            render={({ field }) => (
                                <FormItem>
                                    <FormControl>
                                        <Input
                                            placeholder={t('your-email-address')}
                                            className='w-full !rounded-none text-black'
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <Button
                            type='submit'
                            disabled={isSubmitting}
                            className='bg-red-coral hover:bg-red-coral w-full rounded-none text-sm font-extrabold leading-4 text-white transition-all duration-300 ease-in-out lg:w-auto'
                        >
                            {isSubmitting ? <Loader2 className='size-4 animate-spin' /> : t('subscribe')}
                        </Button>
                    </div>
                </form>
            </Form>
        </div>
    );
};
